/**
 * features/onboarding/draft-storage — LocalStorage draft persistence for the
 * onboarding wizard. Enables "yarım bıraktım, devam ediyorum" recovery.
 *
 * RULES (ADR-108 §6 FE State Management):
 *   - Password is NEVER persisted.
 *   - Drafts older than 24h are discarded on read.
 *   - Storage key is namespaced (`petota-business:onboarding-draft`).
 */
import type {
  AccountStepDraft,
  BranchStepDraft,
  BusinessStepDraft,
  OnboardingDraft,
} from './types';

const STORAGE_KEY = 'petota-business:onboarding-draft';
const TTL_MS = 24 * 60 * 60 * 1000;

function emptyDraft(): OnboardingDraft {
  const now = new Date().toISOString();
  return {
    version: 1,
    updatedAt: now,
    account: {
      email: '',
      displayName: '',
      locale: 'tr',
      acceptTerms: false,
      marketingOptIn: false,
    },
    business: {
      businessName: '',
      taxId: '',
      countryCode: 'TR',
      subVerticalKey: '',
    },
    branch: {
      branchName: '',
      address: {
        street: '',
        city: '',
        district: '',
        postalCode: '',
        countryCode: 'TR',
      },
      timezone: 'Europe/Istanbul',
      phone: '',
    },
  };
}

export function loadOnboardingDraft(): OnboardingDraft {
  if (typeof window === 'undefined') return emptyDraft();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyDraft();
    const parsed = JSON.parse(raw) as OnboardingDraft;
    if (parsed.version !== 1 || !parsed.updatedAt) return emptyDraft();
    const age = Date.now() - new Date(parsed.updatedAt).getTime();
    if (!Number.isFinite(age) || age > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return emptyDraft();
    }
    return { ...emptyDraft(), ...parsed };
  } catch {
    return emptyDraft();
  }
}

export function saveOnboardingDraft(
  draft: Omit<OnboardingDraft, 'version' | 'updatedAt'>,
): void {
  if (typeof window === 'undefined') return;
  const payload: OnboardingDraft = {
    version: 1,
    updatedAt: new Date().toISOString(),
    account: { ...draft.account },
    business: { ...draft.business },
    branch: {
      ...draft.branch,
      address: { ...draft.branch.address },
      operatingHours: draft.branch.operatingHours
        ? draft.branch.operatingHours.map(h => ({ ...h }))
        : undefined,
    },
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function clearOnboardingDraft(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}

export function draftToSubmission(
  draft: OnboardingDraft,
  password: string,
): {
  account: AccountStepDraft;
  business: BusinessStepDraft;
  branch: BranchStepDraft;
} {
  return {
    account: { ...draft.account, password },
    business: { ...draft.business },
    branch: {
      ...draft.branch,
      address: { ...draft.branch.address },
      operatingHours: draft.branch.operatingHours?.map(h => ({ ...h })),
    },
  };
}
